// src/views/pairing/PairingView.tsx
import React, {
  FC, memo, useEffect, useMemo, useState, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { nanoid } from 'nanoid';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import { CardToPair } from '../../components/Pairing/CardToPair';
import { Picture } from '../../components/Pairing/Picture';
import './slice/slice';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { Roles } from '../../constants/Roles';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import './PairingView.less';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { usePolishAudio } from '../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { BASE_API_URL } from '../../services/ApiService';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

gsap.registerPlugin(Draggable);

const MAX_WORDS_PER_PART = 10;
const ANIMATION_DURATION = 0.6;
const POLISH_AUDIO_DELAY = 200; // ms – przerwa między angielskim a polskim słowem
const READING_TIMEOUT = 10000; // ms – bezpiecznik, gdy lektor nie zgłosi końca czytania

// Fisher-Yates shuffle
function shuffleArray<T extends unknown>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

// Inteligentny podział na części - wzorowane na MemoryView
const divideWordsIntoParts = (inputWords: WordItem[]): WordItem[][] => {
  // Tasuj słowa PRZED podziałem na części
  const shuffledWords = shuffleArray(inputWords);
  const wordsCount = shuffledWords.length;

  // Jeśli <= MAX_WORDS_PER_PART, nie dziel
  if (wordsCount <= MAX_WORDS_PER_PART) {
    return [shuffledWords];
  }

  // Oblicz optymalną liczbę części
  const partsCount = Math.ceil(wordsCount / MAX_WORDS_PER_PART);

  // Oblicz bazowy rozmiar każdej części i resztę
  const baseSize = Math.floor(wordsCount / partsCount);
  const remainder = wordsCount % partsCount;

  // Podziel na możliwie równe części
  const dividedWords: WordItem[][] = [];
  let currentIndex = 0;

  for (let i = 0; i < partsCount; i += 1) {
    // Pierwsze 'remainder' części dostają +1 słowo
    const partSize = baseSize + (i < remainder ? 1 : 0);
    dividedWords.push(shuffledWords.slice(currentIndex, currentIndex + partSize));
    currentIndex += partSize;
  }

  return dividedWords;
};

interface WordItem {
  id: string | number;
  term: string;
  image: string;
  definition: string;
}

interface TermItem {
  id: string | number;
  term: string;
}

interface PictureItem {
  id: string | number;
  image: string;
  definition: string;
}

const PairingView: FC = memo(() => {
  const activeList = useSelector(getActiveList);
  const goToNext = useGoToRoute(Path.MEMORY);
  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.PAIRING);

  const [terms, setTerms] = useState<TermItem[]>([]);
  const [pictures, setPictures] = useState<PictureItem[]>([]);
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [listEndInfo, setListEndInfo] = useState(false);
  const [partKey, setPartKey] = useState<string>(nanoid()); // Unikalny klucz dla każdej partii

  const [pairedIds, setPairedIds] = useState<Set<string | number>>(new Set());
  const [matchingIds, setMatchingIds] = useState<Set<string | number>>(new Set());
  const [matchedIds, setMatchedIds] = useState<Set<string | number>>(new Set()); // trafione, czekają na lektora
  const [readingQueue, setReadingQueue] = useState<WordItem[]>([]); // trafione pary w kolejce do czytania

  const { play: playEnglishAudio, stop: stopEnglishAudio } = useAudioPlayer();
  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'pairing_playPolish' });

  // Memoizuj podzielone słowa - przelicz tylko gdy zmienią się words
  const dividedWords = useMemo(
    () => {
      if (!activeList?.words?.length) return [];

      return divideWordsIntoParts(activeList.words);
    },
    [activeList?.words],
  );

  // Funkcja tworzenia danych dla części z tasowaniem
  const createPartData = useCallback((part: WordItem[]) => {
    const currentTerms = shuffleArray(
      part.map((word) => ({
        id: word.id,
        term: word.term,
      })),
    );

    const currentPictures = shuffleArray(
      part.map((word) => ({
        id: word.id,
        image: word.image,
        definition: word.definition,
      })),
    );

    return { currentTerms, currentPictures };
  }, []);

  // Inicjalizacja części przy zmianie activePartIndex lub dividedWords
  useEffect(() => {
    if (dividedWords.length > 0 && activePartIndex < dividedWords.length) {
      const currentPart = dividedWords[activePartIndex];
      const { currentTerms, currentPictures } = createPartData(currentPart);

      setTerms(currentTerms);
      setPictures(currentPictures);
      setPairedIds(new Set());
      setMatchingIds(new Set());
      setMatchedIds(new Set());
      setReadingQueue([]);
    }
  }, [dividedWords, activePartIndex, createPartData]);

  // Znikanie pary z planszy (po przeczytaniu przez lektora)
  const hidePair = useCallback((id: string | number) => {
    setMatchingIds((prev) => new Set(prev).add(id));

    setTimeout(() => {
      setPairedIds((prev) => {
        const newPaired = new Set(prev).add(id);

        // Sprawdź czy wszystkie pary zostały dopasowane
        if (newPaired.size === terms.length) {
          if (activePartIndex < dividedWords.length - 1) {
            // Przejdź do następnej części
            setTimeout(() => {
              setActivePartIndex((prevIndex) => prevIndex + 1);
              setPartKey(nanoid()); // Nowy klucz dla nowej partii
            }, 100);
          } else {
            // Koniec wszystkich części
            setTimeout(() => setListEndInfo(true), 400);
          }
        }

        return newPaired;
      });

      setMatchingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);

        return newSet;
      });
    }, ANIMATION_DURATION * 1000);
  }, [terms.length, activePartIndex, dividedWords.length]);

  // Handler dopasowania pary – para zostaje na planszy i czeka w kolejce na lektora
  const handleMatch = useCallback((id: string | number) => {
    const word: WordItem | undefined = activeList?.words?.find((item: WordItem) => item.id === id);

    // Gra widzi tylko trafienia — plakietka upuszczona gdzie indziej niczego nie zgłasza, więc wynik `null`
    recordAnswer(null, word?.term);

    setMatchedIds((prev) => new Set(prev).add(id));

    if (word) {
      setReadingQueue((prev) => [...prev, word]);
    } else {
      hidePair(id);
    }
  }, [activeList?.words, hidePair]);

  // Czytanie trafionych par po kolei: angielskie słowo, potem polskie.
  // Para znika z planszy dopiero, gdy lektor skończy.
  const currentReading = readingQueue[0];

  useEffect(() => {
    if (!currentReading) {
      return undefined;
    }

    let finished = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const finish = () => {
      if (finished) return;
      finished = true;
      hidePair(currentReading.id);
      setReadingQueue((prev) => prev.filter((word) => word.id !== currentReading.id));
    };

    const playPolishPart = () => {
      timers.push(setTimeout(() => {
        if (!finished) playPolishAudio(currentReading.definition, finish);
      }, POLISH_AUDIO_DELAY));
    };

    timers.push(setTimeout(finish, READING_TIMEOUT));

    const cleanTerm = cleanTextForTTS(currentReading.term);

    if (cleanTerm) {
      playEnglishAudio(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`, playPolishPart);
    } else {
      playPolishPart();
    }

    return () => {
      finished = true;
      timers.forEach(clearTimeout);
      stopEnglishAudio();
      stopPolishAudio();
    };
  }, [currentReading, hidePair, playEnglishAudio, stopEnglishAudio, playPolishAudio, stopPolishAudio]);

  // Restart gry
  const repeatGame = useCallback(() => {
    setActivePartIndex(0);
    setListEndInfo(false);
    setPairedIds(new Set());
    setMatchingIds(new Set());
    setMatchedIds(new Set());
    setReadingQueue([]);

    // Wygeneruj nowe rundy z NOWYM tasowaniem!
    if (!activeList?.words?.length) return;

    const newDividedWords = divideWordsIntoParts(activeList.words);
    const firstPart = newDividedWords[0];
    const { currentTerms, currentPictures } = createPartData(firstPart);

    setTerms(currentTerms);
    setPictures(currentPictures);
    setPartKey(nanoid()); // Nowy klucz dla restartu
  }, [activeList?.words, createPartData]);

  return (
    <>
      <FixWidthContent className="boardForPics">
        {dividedWords.length >= 2 && (
          <p id="pages">
            {activePartIndex + 1} / {dividedWords.length}
          </p>
        )}

        {!listEndInfo && (
          <div key={partKey}>
            {terms.map((term) => (
              <CardToPair
                key={term.id}
                id={term.id}
                content={term.term}
                onMatch={handleMatch}
                isPaired={pairedIds.has(term.id)}
                isMatching={matchingIds.has(term.id)}
              />
            ))}

            {pictures.map((picture) => (
              <Picture
                key={picture.id}
                id={picture.id}
                link={picture.image}
                translation={picture.definition}
                isMatched={matchedIds.has(picture.id)}
                isPaired={pairedIds.has(picture.id)}
                isMatching={matchingIds.has(picture.id)}
              />
            ))}
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} />
        )}

        {!listEndInfo && <UnsplashCredits words={dividedWords[activePartIndex]} />}

        <RightSideButtons>

          <InfoButton content={<RulesOfTheGame />} />

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(PairingView);
